import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldForm } from '@pages/FormCreateOrEdit/components/AddFieldDialog/helpers/getDefaultValue'

import { mapValueTypeToValueFormat } from '@helpers'
import {
  BIND_TYPE,
  DROPDOWN_FILTER_TYPES,
  DURATION_UNIT,
  MODAL_TYPE,
  PREFILL_OPTIONS,
} from '@constants'
import {
  FormRow,
  GETEntityFilter,
  GETShareColumn,
  ModalType,
  MutationType,
  ObjectFieldDTO,
  PayloadCreateField,
} from '@types'
import { v4 as uuid } from 'uuid'

type UseHandlersParams = {
  objectField: ObjectFieldDTO | undefined
  formCode: string
  methods: UseFormReturn<FieldForm, any, undefined>
  modalType: ModalType
  currentRow: GETShareColumn | null | undefined
  editField: MutationType<PayloadCreateField, FormRow> | undefined
  onCloseModal: (isDirty?: boolean | undefined) => void
  addField: MutationType<PayloadCreateField, FormRow>
  defaultValues: FieldForm
}

export const useHandlers = ({
  objectField,
  formCode,
  methods,
  modalType,
  currentRow,
  onCloseModal,
  addField,
  editField: editFieldMutation,
  defaultValues,
}: UseHandlersParams) => {
  const { t } = useTranslation()

  const { reset, watch, setValue, unregister } = methods

  const [isShowFilterDialog, setShowFilterDialog] = useState(false)
  const [currentTypeFilter, setCurrentTypeFilter] = useState<DROPDOWN_FILTER_TYPES | null>(null)
  const [currentFilter, setCurrentFilter] = useState<GETEntityFilter | null>(null)

  const watchBinding = watch('bindType')
  const watchGeneralFilterField = watch('generalFilterField')
  const watchedDropDownListFilters = watch('dropDownListFilters')
  const watchedDropDownWindowFilters = watch('dropDownWindowFilters')

  const handleSave = (data: FieldForm) => {
    const {
      id = Date.now(),
      code,
      title,
      value,
      objectValue,
      objectFormCode,
      bindType = BIND_TYPE.FIELD,
      required,
      params,
      json,
      dropDownListCode,
      dropDownWindowCode,
      dropDownListFilters,
      dropDownWindowFilters,
      isDefaultPlaceholder,
      placeholderValue,
      formatDate,
      isEnabledTimeMenu,
      asCheckbox,
      asDuration,
      durationUnit,
      editField,
      preFillType,
      preFillLink,
      preFillLinkStr,
      preFillFilterName,
      preFillFilterNameStr,
      preFillStaticValue,
      isMultiline,
      fieldValues,
      isAddFieldValues,
      pathArray,
      conditionsDisabled,
      embeddedObjectCode,
      dropDownObjectCode,
      targetPathArray,
    } = data

    const valueFormat = {
      valueFormat: objectField?.valueType
        ? mapValueTypeToValueFormat(objectField?.valueType, asCheckbox, asDuration)
        : null,
    }
    const selectedEmbeddedObjectCode =
      typeof embeddedObjectCode === 'object' && embeddedObjectCode?.id
        ? String(embeddedObjectCode.id)
        : ''
    const selectedDropDownObjectCode =
      typeof dropDownObjectCode === 'object' && dropDownObjectCode?.id
        ? String(dropDownObjectCode.id)
        : ''

    const requestBody: PayloadCreateField = {
      code,
      title,
      value,
      bindType,
      id,
      formCode,
      editField,
      objectValue,
      embeddedObjectCode: selectedEmbeddedObjectCode,
      dropDownObjectCode: selectedDropDownObjectCode || null,
      userRequired: watchBinding === BIND_TYPE.FIELD ? required : null,
      ...(json
        ? { params }
        : {
            objectFormCode:
              typeof objectFormCode === 'object' && objectFormCode?.id
                ? objectFormCode.id.toString()
                : undefined,
          }),
      dropDownListCode:
        typeof dropDownListCode === 'object' && dropDownListCode?.id
          ? (dropDownListCode?.id as string)
          : '',
      dropDownWindowCode:
        typeof dropDownWindowCode === 'object' && dropDownWindowCode?.id
          ? (dropDownWindowCode?.id as string)
          : '',
      generalFilterField: watchGeneralFilterField || null,
      dropDownListFilters: dropDownListFilters || [],
      dropDownWindowFilters: dropDownWindowFilters || [],
      placeholderValue,
      isDefaultPlaceholder,
      formatDate: formatDate ? formatDate.label : null,
      isEnabledTimeMenu,
      preFill: preFillType
        ? {
            preFillType: preFillType.option,
            preFillFilterName:
              preFillType.option === PREFILL_OPTIONS.STATIC ? preFillFilterName : null,
            preFillFilterNameStr:
              preFillType.option === PREFILL_OPTIONS.STATIC ? preFillFilterNameStr : null,
            value: preFillType.option === PREFILL_OPTIONS.STATIC ? preFillStaticValue : null,
            preFillLink: preFillLinkStr ? preFillLink : null,
            preFillLinkStr,
          }
        : null,
      isMultiline,
      fieldValues,
      isAddFieldValues,
      pathArray,
      targetPathArray,
      conditionsDisabled: conditionsDisabled || null,
      durationUnit: asDuration ? durationUnit ?? DURATION_UNIT.SECONDS : null,
      ...valueFormat,
    }

    if (modalType === MODAL_TYPE.EDIT && currentRow) {
      editFieldMutation?.(requestBody)
        .unwrap()
        .then(() => onCloseModal())
        .catch(console.error)

      return
    }

    addField?.(requestBody)
      .unwrap()
      .then(() => onCloseModal())
      .catch(console.error)

    reset(defaultValues)
  }

  const handleDoubleClickChip = (filter: GETEntityFilter, type: DROPDOWN_FILTER_TYPES) => {
    setCurrentFilter(filter)
    setCurrentTypeFilter(type)
    setShowFilterDialog(true)
  }

  const handleAddFilter = (type: DROPDOWN_FILTER_TYPES) => {
    setShowFilterDialog(true)
    setCurrentTypeFilter(type)
  }

  const handleResetFilterDialog = () => {
    setShowFilterDialog(false)
    setCurrentFilter(null)
    setCurrentTypeFilter(null)
  }

  const handleSubmitFilter = (filter: GETEntityFilter) => {
    if (currentTypeFilter) {
      const newFilter = {
        ...filter,
        type: currentTypeFilter,
        internal_id: filter.id ? undefined : filter.internal_id || uuid(),
      }

      const isList = currentTypeFilter === DROPDOWN_FILTER_TYPES.LIST
      const inputName = isList ? 'dropDownListFilters' : 'dropDownWindowFilters'
      const watchedFilters = isList ? watchedDropDownListFilters : watchedDropDownWindowFilters
      const updatedFilters = watchedFilters.map(item => {
        if (
          (filter.id && item.id === filter.id) ||
          (filter.internal_id && item.internal_id === filter.internal_id)
        ) {
          return newFilter
        }

        return item
      })

      if (filter.id || filter.internal_id) {
        setValue(inputName, updatedFilters, {
          shouldDirty: true,
        })

        handleResetFilterDialog()

        return
      }

      setValue(
        inputName,
        [...(isList ? watchedDropDownListFilters : watchedDropDownWindowFilters), newFilter],
        {
          shouldDirty: true,
        }
      )
      handleResetFilterDialog()
    }
  }

  const handleDeleteFilter = (filter: GETEntityFilter, type: DROPDOWN_FILTER_TYPES) => {
    if (type === DROPDOWN_FILTER_TYPES.LIST) {
      setValue(
        'dropDownListFilters',
        watchedDropDownListFilters.filter(({ id, internal_id }) =>
          filter.id ? id !== filter.id : filter.internal_id != internal_id
        ),
        { shouldDirty: true }
      )
    }
    if (type === DROPDOWN_FILTER_TYPES.WINDOW) {
      setValue(
        'dropDownWindowFilters',
        watchedDropDownWindowFilters.filter(({ id, internal_id }) =>
          filter.id ? id !== filter.id : filter.internal_id != internal_id
        ),
        { shouldDirty: true }
      )
    }
  }

  const handleCloseFilterDialog = (isDirty: boolean) => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    handleResetFilterDialog()
  }

  const handleChangeBindType = (value: string | number) => {
    setValue('editField', value === BIND_TYPE.FIELD)

    setValue('value', '')

    setValue('pathArray', [])
    setValue('objectValue', '')
    setValue('targetPathArray', [])
    setValue('targetPathArrayValue', '')
    setValue('dropDownListCode', null)
    setValue('dropDownWindowCode', null)

    unregister('value')
  }

  const handleChangeValue = (_: string | number) => {
    setValue('pathArray', [])
    setValue('objectValue', '')
    setValue('fieldValues', [])
    setValue('targetPathArray', [])
    setValue('targetPathArrayValue', '')
    setValue('embeddedObjectCode', null)
    setValue('dropDownObjectCode', null)
    setValue('dropDownListCode', null)
    setValue('dropDownWindowCode', null)
  }

  return {
    state: {
      isShowFilterDialog,
      currentFilter,
    },
    handlers: {
      handleSave,
      handleDoubleClickChip,
      handleAddFilter,
      handleSubmitFilter,
      handleDeleteFilter,
      handleCloseFilterDialog,
      handleChangeBindType,
      handleChangeValue,
    },
  }
}
